"use client"

import { useEffect } from "react"
import Script from "next/script"
import type { SalesforceChatConfig } from "@/lib/salesforceChat/config"
import { useStandardPromotionBannerOpen } from "@/lib/promotionBannerOpenStore"

type Props = {
  config: SalesforceChatConfig
}

/**
 * Root attribute that globals.css uses to lift the Salesforce chat button above
 * the bottom-docked standard PromotionBanner (see `--promotion-banner-height`).
 */
const SHIFT_ATTRIBUTE = "data-sf-chat-shifted"

export default function SalesforceChatScript({ config }: Props) {
  const cfg = JSON.stringify({ language: "en_US", ...config })
  // Only the standard (full-width) banner overlaps the chat button; the Promo
  // Card variant does not trigger the shift.
  const isStandardBannerOpen = useStandardPromotionBannerOpen()

  useEffect(() => {
    const root = document.documentElement
    if (isStandardBannerOpen) {
      root.setAttribute(SHIFT_ATTRIBUTE, "true")
    } else {
      root.removeAttribute(SHIFT_ATTRIBUTE)
    }
    return () => root.removeAttribute(SHIFT_ATTRIBUTE)
  }, [isStandardBannerOpen])

  return (
    <Script
      id="sf-embedded-messaging"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `(function(){
  var cfg = ${cfg};
  function initEmbeddedMessaging(){
    try {
      embeddedservice_bootstrap.settings.language = cfg.language;
      embeddedservice_bootstrap.init(cfg.orgId, cfg.developerName, cfg.siteUrl, { scrt2URL: cfg.scrt2Url });
    } catch (err) {
      console.error('Error loading Embedded Messaging:', err);
    }
  }
  var s = document.createElement('script');
  s.type = 'text/javascript';
  s.src = cfg.siteUrl + '/assets/js/bootstrap.min.js';
  s.onload = initEmbeddedMessaging;
  s.onerror = function(e){
    console.error('SF Embedded Messaging: bootstrap failed to load. Likely a Salesforce CSP/Trusted-URLs issue for origin ' + window.location.origin, e);
  };
  document.body.appendChild(s);
})();`,
      }}
    />
  )
}
